import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/hooks/useAuth'

/** Platform role: platform_admin (site_admin), platform_support, or null if not staff. */
export function usePlatformRole() {
  const { user } = useAuth()

  const { data: role, isLoading, isError, error } = useQuery({
    queryKey: ['platform-role', user?.id],
    queryFn: async (): Promise<string | null> => {
      if (!user?.id) return null

      // 1. Try RPC (security definer, bypasses RLS)
      const { data: rpcData, error: rpcError } = await supabase.rpc('current_platform_role')
      const parseRole = (val: unknown): string | null => {
        if (typeof val === 'string' && val.trim()) return val.trim()
        if (Array.isArray(val) && val[0] != null) return parseRole(val[0])
        if (val && typeof val === 'object' && 'role' in val) return parseRole((val as { role: unknown }).role)
        if (val && typeof val === 'object' && 'current_platform_role' in val) return parseRole((val as { current_platform_role: unknown }).current_platform_role)
        return null
      }
      if (!rpcError) {
        const rpcRole = parseRole(rpcData)
        if (rpcRole) return rpcRole
      }

      // 2. Fallback: query platform_user_roles directly
      const { data: rowData, error: rowError } = await supabase
        .from('platform_user_roles')
        .select('role')
        .eq('user_id', user.id)
        .maybeSingle()
      if (!rowError && rowData?.role) return String(rowData.role).trim()

      return null
    },
    enabled: !!user?.id,
    retry: false,
  })

  // Env override for debugging; mock Supabase in dev
  const forceAdmin = import.meta.env.VITE_FORCE_PLATFORM_ADMIN === 'true'
  const isMock = !import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY
  const devBypass = import.meta.env.DEV && isMock

  const canSeeClients = forceAdmin || devBypass || role === 'platform_admin' || role === 'platform_support'

  return {
    role,
    isSiteAdmin: devBypass || role === 'platform_admin',
    isPlatformStaff: role === 'platform_admin' || role === 'platform_support',
    canSeeClients,
    isLoading,
    isError,
    error,
  }
}
